'use client'

// Custom tooltip that doesn't interfere with flex layout
const BarTooltip = ({ message, children, style }) => (
  <div className="group relative" style={style}>
    {children}
    <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 scale-0 transition-all rounded-sm border border-border bg-card p-2 text-xs text-foreground shadow-sm group-hover:scale-100 z-50 text-center">
      {message}
    </span>
  </div>
)

export default function BiasBars({ sources, hasPercentages = false }) {
  const biasMeta = {
    left: {
      color: 'bg-blue-600',
      textColor: 'text-white',
      is: (bias) => bias < -6,
      label: 'Left',
      tooltip: 'Left-leaning outlets tend to favor progressive policies and Democratic viewpoints.',
    },
    center: {
      color: 'bg-muted',
      textColor: 'text-foreground',
      is: (bias) => bias >= -6 && bias <= 6,
      label: 'Center',
      tooltip: 'Center outlets aim for balanced reporting with minimal partisan bias.',
    },
    right: {
      color: 'bg-red-600',
      textColor: 'text-white',
      is: (bias) => bias > 6,
      label: 'Right',
      tooltip: 'Right-leaning outlets tend to favor conservative policies and Republican viewpoints.',
    },
  }

  const categorizeArticles = (sources) => {
    const categories = {
      left: { count: 0 },
      center: { count: 0 },
      right: { count: 0 },
    }

    if (!sources) return categories

    sources.forEach((source) => {
      const bias = source.outlet?.bias || source.bias

      if (typeof bias !== 'number') return

      for (const [categoryName, meta] of Object.entries(biasMeta)) {
        if (meta.is(bias)) {
          categories[categoryName].count++
          break
        }
      }
    })

    return categories
  }

  // Helper function to round percentages so they add up to 100
  const roundPercentages = (categories, total) => {
    const percentages = {}
    const rounded = {}
    let sum = 0

    Object.keys(categories).forEach((key) => {
      if (categories[key].count > 0) {
        percentages[key] = (categories[key].count / total) * 100
        rounded[key] = Math.round(percentages[key])
        sum += rounded[key]
      }
    })

    if (sum !== 100 && Object.keys(rounded).length > 0) {
      const diff = 100 - sum
      const largestKey = Object.keys(rounded).reduce((a, b) =>
        (categories[a]?.count || 0) > (categories[b]?.count || 0) ? a : b
      )
      rounded[largestKey] += diff
    }

    return rounded
  }

  const renderBars = (categories, total) => {
    const roundedPercentages = roundPercentages(categories, total)

    return Object.entries(biasMeta).map(([categoryName, meta], index, arr) => {
      const category = categories[categoryName]
      if (category.count === 0) return null

      const percentage = (category.count / total) * 100
      const roundedPercentage = roundedPercentages[categoryName]

      const roundedClass =
        index === 0
          ? 'rounded-l-sm'
          : index === arr.length - 1
            ? 'rounded-r-sm'
            : ''

      const sourceLabels = {
        left: 'left-leaning',
        center: 'centrist',
        right: 'right-leaning',
      }
      const tooltipMessage = `${category.count} of ${total} articles on this topic (${roundedPercentage}%) were written by ${sourceLabels[categoryName]} sources.`

      return (
        <BarTooltip
          key={categoryName}
          message={tooltipMessage}
          style={{ flex: percentage }}
        >
          <div
            className={`h-full ${meta.color} ${roundedClass} ${hasPercentages ? 'px-1.5 py-1' : ''}`}
          >
            {hasPercentages && (
              <span
                className={`flex items-center justify-center text-[10px] font-sans font-bold tracking-wider uppercase whitespace-nowrap ${meta.textColor}`}
              >
                {meta.label[0]} {roundedPercentage}%
              </span>
            )}
          </div>
        </BarTooltip>
      )
    })
  }

  const categories = categorizeArticles(sources)
  const totalSources = sources?.length || 0

  return (
    <div
      className={`w-full flex gap-0 rounded-sm border border-border ${hasPercentages ? 'h-auto' : 'h-2'}`}
    >
      {totalSources > 0 ? renderBars(categories, totalSources) : null}
    </div>
  )
}
